import React, { useContext, useState, useEffect, useMemo } from "react";
import MapContext from "../../MapContext";
import { Checkbox } from "@progress/kendo-react-inputs";
import "./Layers.css";
import { getMapLayerById, toggleVisibility } from "../../utils/mapUtils";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { useDispatch, useSelector } from "react-redux";
import {
    updateLayerVisible,
    addLayerToVisProperties,
    updateGroupVisible,
} from "../../../redux/setConfigSlice";
import { Button } from "@progress/kendo-react-buttons";
import { setActiveLayer } from "../../../redux/optionsSlice";
import LayerOptions from "./LayerOptions";
import { BiListPlus } from "react-icons/bi";

export const PanelContent = ({ props, setChildrenDisabled, lastChild, legendProps }) => {
    const { map } = useContext(MapContext);
    const dispatch = useDispatch();
    const layerVis = useSelector((state) => state.configData.layerVisProperties);
    const groupVis = useSelector((state) => state.configData.groupVisProperties);

    const recursivelyGrabLayers = (item) => {
        let newItem = item;
        if (item.hasOwnProperty("SubLayers")) {
            newItem = item.SubLayers;
            recursivelyGrabLayers(newItem);
        }
        return newItem;
    };

    const idsToToggleVisible = () => {
        const layers = recursivelyGrabLayers(props);
        const ids = [];
        if (layers.length) {
            layers.forEach((layer) => ids.push(layer.Id));
        } else if (layers.hasOwnProperty("Id")) {
            ids.push(layers.Id);
        } else if (layers.hasOwnProperty("DisplayName")) {
            ids.push(layers.DisplayName);
        }
        return ids;
    };

    const handleCheck = (e) => {
        const ids = idsToToggleVisible();
        const isChecked = e.target.checked;
        if (ids.length === 1 && props.Type !== "Group") {
            handleSingleLayerCheck(ids[0], isChecked);
        } else if (!isChecked) {
            handleParentLayerOff(ids);
            setChildrenDisabled(isChecked);
        } else if (isChecked) {
            handleParentLayerOn(ids);
            setChildrenDisabled(isChecked);
        } else {
            console.error("handle edge case for checking layers on and off");
        }
        e.stopPropagation();
    };

    /**
     * Updates the visibility of a single layer on the map.
     *
     * @param {string} id - The ID of the layer to update.
     * @param {boolean} checked - The new visibility state of the layer.
     * @returns {void}
     */
    const handleSingleLayerCheck = (id, checked) => {
        dispatch(updateLayerVisible([id, checked]));
        toggleVisibility(map, id, checked);
    };

    /**
     * Updates the visibility of layers in a group to false.
     *
     * @param {Array} ids - An array of layer IDs.
     * @returns {void}
     */
    const handleParentLayerOff = (ids) => {
        dispatch(updateGroupVisible([props.DisplayName, false]));
        ids.forEach((id) => {
            toggleVisibility(map, id, false);
        });
    };

    /**
     * Updates the visibility of layers in a group to true.
     *
     * @param {Array} ids - An array of layer IDs.
     * @returns {void}
     */
    const handleParentLayerOn = (ids) => {
        dispatch(updateGroupVisible([props.DisplayName, true]));
        ids.forEach((id) => {
            const vis = layerVis.filter((layer) => layer.Id === id);
            toggleVisibility(map, id, vis[0].Visible);
        });
    };

    const goToOptions = () => {
        const id = props.Id ? props.Id : props.DisplayName;
        dispatch(setActiveLayer({ layerId: id, layerName: props.DisplayName }));
    };

    const isChecked = props.hasOwnProperty("SubLayers")
        ? groupVis.find((group) => group?.DisplayName === props?.DisplayName)?.Visible
        : layerVis.find((layer) => layer?.Id === (props?.Id ?? props?.DisplayName))
              ?.Visible;

    return (
        <div className={`layer-control ${lastChild ? "last-child" : ""}`}>
            <Checkbox
                style={{ paddingLeft: "4px" }}
                value={isChecked}
                onClick={handleCheck}
            />
            {legendProps?.icon ? (
                // Display multilegend icon
                <span className="legend-icon-container">
                    <BiListPlus />
                </span>
            ) : legendProps?.imageUrl ? (
                // Display legend image from geoserver
                <span className="legend-icon-container">
                    <img src={legendProps.imageUrl} />
                </span>
            ) : null}
            <span className="layer-title">{props.DisplayName}</span>
            {lastChild ? (
                <Button
                    className="options"
                    icon="chevron-right"
                    fillMode="flat"
                    onClick={goToOptions}
                />
            ) : (
                <div />
            )}
        </div>
    );
};
export const TopLevelPanel = ({ props, parentChecked }) => {
    const groupVisProperties = useSelector(
        (state) => state.configData.groupVisProperties
    );
    const [childrenDisabled, setChildrenDisabled] = useState();

    const expanded = props.IsExpanded ?? false;
    const disabled = parentChecked === undefined ? false : parentChecked;
    const authorizedLayers = useSelector(
        (state) => state.authorizedPermissions.layerPermissions
    );
    const groupPermissions = useSelector(
        (state) => state.authorizedPermissions.groupPermissions
    );
    const hideGroup = shouldHideGroup(props, groupPermissions);
    // check if layer is readable by checking for permission
    const layerReadPermission = authorizedLayers?.find((l) => l.layerId === props.Id)
        ?.permissions?.Read;
    // if no permission, layer is readable, if permission use Read value
    const canReadLayer = layerReadPermission === undefined ? true : layerReadPermission;
    const [legendUrl, setLegendUrl] = useState({});
    const [legendProps, setLegendProps] = useState();

    // Group layer property dictates childrenDisabled state
    useEffect(() => {
        if (props.hasOwnProperty("SubLayers")) {
            // Match props to groupVisProperties
            const matched = groupVisProperties.find(
                (g) => g.DisplayName === props.DisplayName
            );
            setChildrenDisabled(!matched.Visible);
        }
    }, [groupVisProperties]);

    // Set legend items
    useEffect(() => {
        // Handle label according to layer type
        const label = createGeoserverLabelProperties(props);
        if (!label) {
            return;
        }
        const requestString = `${label.URL}?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&LAYER=${label.Layer}&STYLE=${label.Style}&legend_options=wrap:true;wrap_limit:248;`;
        const img = new Image();
        img.src = requestString;
        img.onload = () => {
            if (img.width < 30 && img.height < 21) {
                setLegendProps({ imageUrl: requestString });
            } else {
                setLegendUrl({
                    imageUrl: requestString,
                    props: { className: "legend-dropdown" },
                });
                setLegendProps({ icon: true });
            }
        };
    }, []);

    return props.hasOwnProperty("SubLayers") && hideGroup === false ? (
        <PanelBar dir="rtl" selected={"false"}>
            <PanelBarItem
                key={props.DisplayName}
                title={
                    <PanelContent
                        props={props}
                        setChildrenDisabled={setChildrenDisabled}
                    />
                }
                expanded={expanded}>
                {makeArray(props.SubLayers).map((item) => {
                    return (
                        <TopLevelPanel
                            key={item.DisplayName}
                            props={item}
                            parentChecked={childrenDisabled}
                        />
                    );
                })}
            </PanelBarItem>
        </PanelBar>
    ) : props.hasOwnProperty("SubLayers") === false && canReadLayer ? (
        <PanelBar dir="rtl" selected="false">
            <PanelBarItem
                props={props}
                key={props.DisplayName}
                disabled={disabled}
                expanded={expanded}
                {...legendUrl?.props}
                title={
                    <PanelContent
                        props={props}
                        lastChild={true}
                        legendProps={legendProps}
                    />
                }>
                {legendUrl?.imageUrl ? (
                    <div className="legend-container">
                        <img src={legendUrl.imageUrl} />
                    </div>
                ) : null}
            </PanelBarItem>
        </PanelBar>
    ) : // return nothing if layer is not readable
    null;
};

const Layers = () => {
    const dispatch = useDispatch();
    const layers = useSelector((state) => state.configData.layers);
    const layerOptions = useSelector((state) => state.layerOptions);
    const staticBaseMaps = useSelector((state) => state.baseMapsConfig.baseMapLayer);

    useEffect(() => {
        dispatch(addLayerToVisProperties(staticBaseMaps));
    }, [staticBaseMaps]);

    return (
        <>
            {layers ? (
                <>
                    {layerOptions.layerId && <LayerOptions />}
                    <div
                        style={layerOptions.layerId ? { display: "none" } : null}
                        id="layers">
                        {/* handle group layers */}
                        {layers.map((group) => {
                            return (
                                <TopLevelPanel key={group.DisplayName} props={group} />
                            );
                        })}
                        {staticBaseMaps.map((group) => {
                            return (
                                <TopLevelPanel key={group.DisplayName} props={group} />
                            );
                        })}
                    </div>
                </>
            ) : (
                <div>loading</div>
            )}
        </>
    );
};

export default Layers;

// takes array or object and returns array
export const makeArray = (inData) => {
    return Array.isArray(inData) ? inData : [inData];
};

/**
 * Check to see if group layer should be rendered by checking
 *  all sublayers. If all sublayers are not readable return true
 *  else false
 * @param {object} props Properties for layer or group
 * @param {import("../../../permissionUtils").MergedGroupPermission[]} authorizedGroups Permissions for groups
 * @returns {boolean} Indicating whether or not to show layer
 */
const shouldHideGroup = (props, authorizedGroups) => {
    let hideGroup = false;
    if (props.SubLayers) {
        const groupPermission = authorizedGroups?.find(
            (g) => g.groupId === props.DisplayName
        );
        if (groupPermission?.permissions?.Read === false) {
            hideGroup = true;
        }
    }
    return hideGroup;
};

/**
 * Creates and validates properties for a geoserver label.
 * @param {object} props - An object containing the properties of the layer, including the type, URL, Layer, and Style.
 * @returns {object|null} - An object containing the validated label properties, including the URL, Layer, and Style. Returns null if the label properties are invalid.
 */
const createGeoserverLabelProperties = (props) => {
    switch (props.Type) {
        case "GeoJson":
            return createLabelProperties({
                ...props.Label,
                DisplayName: props.DisplayName,
            });
        case "WMS":
            return createLabelProperties(props);
        case "Group":
            return null;
        case "ArcGISImageService":
            return null;
        case "ArcGISMapService":
            return null;
        default:
            console.log(
                `Legend for layer: ${props.DisplayName}, type: ${props.Type} unaccounted for`
            );
    }
};

/**
 * Creates label properties based on the given props object and validates them.
 * @param {object} props - An object containing the properties of the layer, including the URL, Layer, and Style.
 * @returns {object|null} - An object containing the validated label properties, including the URL, Layer, and Style. Returns null if the label properties are invalid.
 */
const createLabelProperties = (props) => {
    if (!props?.URL || !props?.Layer || !props?.Style) return null;
    const { URL, Layer, Style } = props;
    const label = { URL, Layer, Style };
    const isValid = validateLabelProperties(label);
    if (isValid) {
        return label;
    } else {
        console.log(`Cannot create legend for ${props.DisplayName}`);
        return null;
    }
};

/**
 * Validates the label properties to ensure they are of the correct type.
 * @param {object} label - An object containing the label properties to be validated.
 * @returns {boolean} - True if the label properties are valid, false otherwise.
 */
const validateLabelProperties = (label) => {
    let isValid = true;
    Object.keys(label).forEach((l) => {
        if (typeof label[l] !== "string") {
            console.log(`Invalid legend property: ${l}`);
            isValid = false;
        }
    });
    return isValid;
};
